interface GlobalDraftPromptArgs {
    isGlobalDraftEnabled: boolean;
    serverDraftId?: string;
    serverDraftVersion?: string | null;
    loadedDraftId?: string | null;
    loadedDraftVersion?: string | null;
    hasServerContent: boolean;
}

interface DraftRange {
    id: string;
    start?: string;
    end?: string;
}

interface PayrollCycleDraftValidationArgs {
    isGlobalDraftEnabled: boolean;
    previousPayrollStart?: string | null;
    nextPayrollStart?: string | null;
}

interface RosterReloadDraftValidationArgs {
    isGlobalDraftEnabled: boolean;
}

interface RosterReloadPromptArgs {
    isGlobalDraftEnabled: boolean;
    serverDraftId?: string | null;
    serverDraftVersion?: string | null;
    loadedDraftId?: string | null;
    loadedDraftVersion?: string | null;
    hasServerContent: boolean;
    forcePrompt?: boolean;
}

interface UnsavedChangesNavigationPromptArgs {
    isGlobalDraftEnabled: boolean;
    hasUnsavedChanges: boolean;
    draftStatus?: string | null;
}

interface SavedGlobalDraftRefreshPromptArgs {
    isGlobalDraftEnabled: boolean;
    forcePrompt: boolean;
    savedDraftId?: string | null;
}

interface LoadDraftPromptAfterValidationArgs {
    draftStatus?: string | null;
    rosterValidationApplied: boolean;
}

interface LoadedGlobalDraftMarkerArgs {
    isGlobalDraftEnabled: boolean;
    loadedPayrollStart?: string | null;
    nextPayrollStart?: string | null;
}

interface AppliedGlobalDraftRangeArgs {
    isGlobalDraftEnabled: boolean;
    loadedPayrollStart?: string | null;
    targetPayrollStart?: string | null;
    isAppliedToPayroll?: boolean;
}

interface RetainedGlobalDraftVisibleRangeArgs {
    isGlobalDraftEnabled: boolean;
    hasFullPayrollSnapshot?: boolean;
    loadedPayrollStart?: string | null;
    targetPayrollStart?: string | null;
    loadedRangeStart?: Date | null;
    loadedRangeEnd?: Date | null;
    visibleRangeStart?: Date | null;
    visibleRangeEnd?: Date | null;
}

interface AppliedGlobalDraftStatusArgs extends AppliedGlobalDraftRangeArgs {
    hasServerContent: boolean;
    serverDraftId?: string | null;
    serverDraftVersion?: string | null;
    loadedDraftId?: string | null;
    loadedDraftVersion?: string | null;
}

interface DraftSaveNavigationBlockArgs {
    isGlobalDraftEnabled: boolean;
    isSavingDraft: boolean;
}

interface RosterViewToggleDisabledArgs {
    loadingPayrollData: boolean;
    payrollNavigationDisabled: boolean;
}

interface SuccessfulBulkPublishReloadArgs {
    isGlobalDraftEnabled: boolean;
}

interface GlobalDraftRequestCacheArgs {
    isGlobalDraftEnabled: boolean;
    draftVersion?: string | null;
    fallbackKey?: string | null;
}

interface AsyncRosterLoadResultArgs {
    activeToken: number;
    requestToken: number;
}

interface DiscoveredDraftActivationArgs {
    isGlobalDraftEnabled: boolean;
}

interface GlobalDraftMergeShift {
    id: string;
    resourceId?: string | null;
    originalResourceID?: string | null;
    BookingId?: string | null;
    changeStatus?: number | null;
    publishedVersion?: string | null;
    loading?: boolean;
    validating?: boolean;
}

interface GlobalDraftShiftMergeArgs<TShift extends GlobalDraftMergeShift> {
    draftShifts: TShift[];
    currentShifts: TShift[];
    draftVersion?: string | null;
    normalizeDraftShift?: (shift: TShift) => TShift;
    unassignShift?: (shift: TShift) => TShift;
}

const latestDraftMessage = "Latest Draft Version is available to load";
const syncedDraftStatus = "synced";
const foundDraftStatus = "found";
const unchangedChangeStatus = 0;

const getStorageKeyPart = (value: string | undefined) => value?.trim() || "unknown";
const getLoadedGlobalDraftKey = (siteId: string, userId: string | undefined) =>
    `global_draft_${getStorageKeyPart(siteId)}_${getStorageKeyPart(userId)}`;
const getLoadedGlobalDraftVersionKey = (siteId: string, userId: string | undefined) =>
    `${getLoadedGlobalDraftKey(siteId, userId)}_version`;

const shouldShowGlobalDraftAvailablePrompt = ({
    isGlobalDraftEnabled,
    serverDraftId,
    serverDraftVersion,
    loadedDraftId,
    loadedDraftVersion,
    hasServerContent,
}: GlobalDraftPromptArgs) => {
    const hasSameLoadedDraft = serverDraftId === loadedDraftId
        && (!serverDraftVersion || serverDraftVersion === loadedDraftVersion);

    return isGlobalDraftEnabled
        && hasServerContent
        && !!serverDraftId
        && !hasSameLoadedDraft;
}

const shouldKeepAppliedGlobalDraftForPayroll = ({
    isGlobalDraftEnabled,
    loadedPayrollStart,
    targetPayrollStart,
    isAppliedToPayroll,
}: AppliedGlobalDraftRangeArgs) => {
    return isGlobalDraftEnabled
        && isAppliedToPayroll === true
        && !!targetPayrollStart
        && loadedPayrollStart === targetPayrollStart;
}

const shouldTreatRetainedGlobalDraftRangeAsLoaded = ({
    isGlobalDraftEnabled,
    hasFullPayrollSnapshot,
    loadedPayrollStart,
    targetPayrollStart,
    loadedRangeStart,
    loadedRangeEnd,
    visibleRangeStart,
    visibleRangeEnd,
}: RetainedGlobalDraftVisibleRangeArgs) => {
    if (!isGlobalDraftEnabled
        || !hasFullPayrollSnapshot
        || !targetPayrollStart
        || loadedPayrollStart !== targetPayrollStart) {
        return false;
    }

    if (!loadedRangeStart || !loadedRangeEnd || !visibleRangeStart || !visibleRangeEnd) {
        return false;
    }

    return visibleRangeStart.getTime() >= loadedRangeStart.getTime()
        && visibleRangeEnd.getTime() <= loadedRangeEnd.getTime();
}

const shouldKeepAppliedGlobalDraftStatus = ({
    hasServerContent,
    serverDraftId,
    serverDraftVersion,
    loadedDraftId,
    loadedDraftVersion,
    ...rangeArgs
}: AppliedGlobalDraftStatusArgs) => {
    const hasSameLoadedDraft = serverDraftId === loadedDraftId
        && (!serverDraftVersion || serverDraftVersion === loadedDraftVersion);

    return hasServerContent
        && !!serverDraftId
        && hasSameLoadedDraft
        && shouldKeepAppliedGlobalDraftForPayroll(rangeArgs);
}

const isDateWithinDraftRange = (date: string | undefined, draft: DraftRange) => {
    return !!date
        && !!draft.start
        && !!draft.end
        && date >= draft.start
        && date <= draft.end;
}

const findDraftForPayrollRange = <TDraft extends DraftRange>(
    drafts: TDraft[],
    visibleStart: string,
    payrollStart?: string,
) => {
    return drafts.find(draft => {
        return draft.start === visibleStart
            || (!!payrollStart && draft.start === payrollStart)
            || isDateWithinDraftRange(visibleStart, draft)
            || isDateWithinDraftRange(payrollStart, draft);
    });
}

const shouldActivateDiscoveredDraftImmediately = ({
    isGlobalDraftEnabled,
}: DiscoveredDraftActivationArgs) => {
    return !isGlobalDraftEnabled;
}

const hasAssignedResource = (shift: GlobalDraftMergeShift) => {
    return !!shift.resourceId && shift.resourceId !== "0";
}

const isCurrentPublishedAssignment = (shift: GlobalDraftMergeShift) => {
    return shift.changeStatus === unchangedChangeStatus
        && hasAssignedResource(shift)
        && !!shift.BookingId;
}

const isPublishedAfterDraft = (shift: GlobalDraftMergeShift, draftVersion?: string | null) => {
    if (!isCurrentPublishedAssignment(shift) || !shift.publishedVersion || !draftVersion) {
        return false;
    }

    const publishedTime = new Date(shift.publishedVersion).getTime();
    const draftTime = new Date(draftVersion).getTime();
    return Number.isFinite(publishedTime)
        && Number.isFinite(draftTime)
        && publishedTime > draftTime;
}

const clearTransientDraftLoadState = <TShift extends GlobalDraftMergeShift>(shift: TShift) => ({
    ...shift,
    loading: false,
    validating: false,
} as TShift);

const mergeGlobalDraftShiftStores = <TShift extends GlobalDraftMergeShift>({
    draftShifts,
    currentShifts,
    draftVersion,
    normalizeDraftShift,
    unassignShift,
}: GlobalDraftShiftMergeArgs<TShift>) => {
    const finalShifts: { [key: string]: TShift } = {};
    const currentShiftsById = new Map<string, TShift>();
    const normalizeDraft = normalizeDraftShift ?? ((shift: TShift) => shift);
    const unassign = unassignShift ?? ((shift: TShift) => ({ ...shift, resourceId: "0" } as TShift));

    currentShifts.forEach(shift => {
        currentShiftsById.set(shift.id, shift);
    });

    draftShifts.forEach(shift => {
        const currentShift = currentShiftsById.get(shift.id);
        if (currentShift && isPublishedAfterDraft(currentShift, draftVersion)) {
            finalShifts[currentShift.id] = clearTransientDraftLoadState(currentShift);
            return;
        }

        const normalizedShift = normalizeDraft(shift);
        finalShifts[normalizedShift.id] = clearTransientDraftLoadState(normalizedShift);
    });

    currentShifts.forEach(shift => {
        if (finalShifts[shift.id]) return;

        const mergedShift = isCurrentPublishedAssignment(shift)
            ? shift
            : unassign(shift);
        finalShifts[shift.id] = clearTransientDraftLoadState(mergedShift);
    });

    return finalShifts;
}

const shouldValidateGlobalDraftOnPayrollCycleChange = ({
    isGlobalDraftEnabled,
    previousPayrollStart,
    nextPayrollStart,
}: PayrollCycleDraftValidationArgs) => {
    return isGlobalDraftEnabled
        && !!nextPayrollStart
        && previousPayrollStart !== nextPayrollStart;
}

const shouldValidateGlobalDraftOnRosterReload = ({
    isGlobalDraftEnabled,
}: RosterReloadDraftValidationArgs) => {
    return isGlobalDraftEnabled;
}

const shouldPromptGlobalDraftOnRosterReload = ({
    isGlobalDraftEnabled,
    serverDraftId,
    serverDraftVersion,
    loadedDraftId,
    loadedDraftVersion,
    hasServerContent,
    forcePrompt,
}: RosterReloadPromptArgs) => {
    if (forcePrompt) {
        return isGlobalDraftEnabled
            && hasServerContent
            && !!serverDraftId;
    }

    return shouldShowGlobalDraftAvailablePrompt({
        isGlobalDraftEnabled,
        serverDraftId: serverDraftId ?? undefined,
        serverDraftVersion,
        loadedDraftId,
        loadedDraftVersion,
        hasServerContent,
    });
}

const shouldPromptSavedGlobalDraftAfterRefresh = ({
    isGlobalDraftEnabled,
    forcePrompt,
    savedDraftId,
}: SavedGlobalDraftRefreshPromptArgs) => {
    return isGlobalDraftEnabled
        && forcePrompt
        && !!savedDraftId;
}

const shouldShowLoadDraftPromptAfterRosterValidation = ({
    draftStatus,
    rosterValidationApplied,
}: LoadDraftPromptAfterValidationArgs) => {
    return draftStatus === foundDraftStatus && rosterValidationApplied;
}

const shouldShowUnsavedChangesNavigationPrompt = ({
    isGlobalDraftEnabled,
    hasUnsavedChanges,
    draftStatus,
}: UnsavedChangesNavigationPromptArgs) => {
    if (!hasUnsavedChanges) {
        return false;
    }

    return !(isGlobalDraftEnabled && draftStatus === syncedDraftStatus);
}

const shouldWarnBeforeBrowserUnload = (args: UnsavedChangesNavigationPromptArgs) => {
    return shouldShowUnsavedChangesNavigationPrompt(args);
}

const createUnsavedChangesPromptOptions = (confirmButtonColor: string, cancelButtonColor: string) => ({
    title: 'Unsaved Changes',
    text: 'You have unsaved changes. Click "Yes" to save draft before navigating, or "No" to navigate without saving.',
    icon: 'warning' as const,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    cancelButtonColor,
    confirmButtonColor,
});

const shouldClearLoadedGlobalDraftMarkerOnPayrollCycleChange = ({
    isGlobalDraftEnabled,
    loadedPayrollStart,
    nextPayrollStart,
}: LoadedGlobalDraftMarkerArgs) => {
    return isGlobalDraftEnabled
        && !!loadedPayrollStart
        && !!nextPayrollStart
        && loadedPayrollStart !== nextPayrollStart;
}

const shouldBlockPayrollNavigationDuringDraftSave = ({
    isGlobalDraftEnabled,
    isSavingDraft,
}: DraftSaveNavigationBlockArgs) => {
    return isGlobalDraftEnabled && isSavingDraft;
}

const shouldDisableRosterViewToggle = ({
    loadingPayrollData,
    payrollNavigationDisabled,
}: RosterViewToggleDisabledArgs) => {
    return loadingPayrollData || payrollNavigationDisabled;
}

const shouldReloadRosterAfterSuccessfulBulkPublish = ({
    isGlobalDraftEnabled,
}: SuccessfulBulkPublishReloadArgs) => {
    return isGlobalDraftEnabled;
}

const getGlobalDraftRequestCacheParams = ({
    isGlobalDraftEnabled,
    draftVersion,
    fallbackKey,
}: GlobalDraftRequestCacheArgs) => {
    if (!isGlobalDraftEnabled) {
        return undefined;
    }

    return {
        globalDraftVersion: draftVersion || fallbackKey || `${Date.now()}`,
    };
}

const shouldApplyAsyncRosterLoadResult = ({
    activeToken,
    requestToken,
}: AsyncRosterLoadResultArgs) => {
    return activeToken === requestToken;
}

const createLoadDraftPromptOptions = (isGlobalDraftEnabled: boolean) => {
    if (isGlobalDraftEnabled) {
        return {
            text: latestDraftMessage,
            icon: "info" as const,
            showDenyButton: false,
            confirmButtonText: "Load Draft",
            confirmButtonColor: "green",
        };
    }

    return {
        title: "Load Draft?",
        text: "Do you want to load the saved draft?",
        icon: "info" as const,
        showDenyButton: true,
        confirmButtonText: "Yes",
        denyButtonText: "No",
        confirmButtonColor: "green",
        denyButtonColor: "red",
    };
}

export {
    createLoadDraftPromptOptions,
    createUnsavedChangesPromptOptions,
    findDraftForPayrollRange,
    getGlobalDraftRequestCacheParams,
    getLoadedGlobalDraftKey,
    getLoadedGlobalDraftVersionKey,
    mergeGlobalDraftShiftStores,
    shouldApplyAsyncRosterLoadResult,
    shouldActivateDiscoveredDraftImmediately,
    shouldBlockPayrollNavigationDuringDraftSave,
    shouldClearLoadedGlobalDraftMarkerOnPayrollCycleChange,
    shouldDisableRosterViewToggle,
    shouldKeepAppliedGlobalDraftForPayroll,
    shouldTreatRetainedGlobalDraftRangeAsLoaded,
    shouldKeepAppliedGlobalDraftStatus,
    shouldReloadRosterAfterSuccessfulBulkPublish,
    shouldPromptSavedGlobalDraftAfterRefresh,
    shouldPromptGlobalDraftOnRosterReload,
    shouldShowLoadDraftPromptAfterRosterValidation,
    shouldValidateGlobalDraftOnRosterReload,
    shouldValidateGlobalDraftOnPayrollCycleChange,
    shouldShowUnsavedChangesNavigationPrompt,
    shouldWarnBeforeBrowserUnload,
    shouldShowGlobalDraftAvailablePrompt,
};
